import './src/styles/global.css';
import type { GatsbyBrowser, ShouldUpdateScrollArgs } from 'gatsby';

/**
 * Controls the scroll position on route changes. This implementation ensures
 * that navigation to a new page always scrolls the user to the top, while
 * preserving smooth scrolling for on-page hash links.
 *
 * @param {ShouldUpdateScrollArgs} args - Gatsby's scroll update arguments.
 * @returns {boolean} - A boolean indicating whether Gatsby should handle scrolling.
 */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  routerProps: { location },
  prevRouterProps,
  getSavedScrollPosition,
}) => {
  // Let Gatsby handle scrolling for on-page hash links (e.g., on the homepage)
  // This allows the CSS `scroll-behavior: smooth` to work as intended.
  if (prevRouterProps && location.pathname === prevRouterProps.location.pathname) {
    return true;
  }

  // For back/forward browser actions, restore the saved position instantly.
  const savedPosition = getSavedScrollPosition(location);
  if (savedPosition) {
    // Using window.scrollTo with 'instant' behavior to override CSS smooth scrolling.
    window.scrollTo({
      top: savedPosition[1],
      left: savedPosition[0],
      behavior: 'instant',
    });
    return false; // Prevent Gatsby's default scroll handling
  }

  // For all other new page navigations, scroll to the top instantly.
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  return false; // Prevent Gatsby's default scroll handling
};
